import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle2Icon, Clock3Icon, MailIcon, MapPinIcon, PhoneIcon, SendIcon } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { Header } from '../components/Header';
import { SectionHeading } from '../components/SectionHeading';
type InquiryForm = {
  name: string;
  email: string;
  phone: string;
  travelDates: string;
  travellers: string;
  interests: string;
  message: string;
};
type FormErrors = Partial<Record<keyof InquiryForm, string>>;
const initialForm: InquiryForm = {
  name: '',
  email: '',
  phone: '',
  travelDates: '',
  travellers: '',
  interests: '',
  message: ''
};
const inputClass = 'w-full border border-[#cfd3c8] bg-[#f8f5ed] px-3 py-2.5 text-base font-normal text-[#17362e] outline-none transition-colors placeholder:text-[#718074] focus:border-[#4f8057] focus:ring-2 focus:ring-[#d8ebcd]';
export function ContactUs() {
  const [form, setForm] = useState<InquiryForm>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const formHeadingRef = useRef<HTMLHeadingElement>(null);
  const location = useLocation();
  useEffect(() => {
    if (location.hash === '#plan-trip') {
      window.setTimeout(() => formHeadingRef.current?.focus(), 100);
    }
  }, [location.hash]);
  function updateField(field: keyof InquiryForm, value: string) {
    setForm(current => ({
      ...current,
      [field]: value
    }));
    setErrors(current => ({
      ...current,
      [field]: undefined
    }));
    setSubmitted(false);
  }
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: FormErrors = {};
    if (!form.name.trim()) nextErrors.name = 'Please share your name.';
    if (!form.email.trim()) {
      nextErrors.email = 'Please share your email address.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      nextErrors.email = 'Enter a valid email address.';
    }
    if (!form.interests) {
      nextErrors.interests = 'Choose the kind of journey you are considering.';
    }
    if (!form.message.trim()) {
      nextErrors.message = 'Tell us a little about the trip you have in mind.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      setSubmitted(true);
      setForm(initialForm);
    }
  }
  return <div className="min-h-screen w-full bg-[#f8f5ed] text-[#17362e]" data-id="element-336">
      <Header data-id="element-337" />
      <main data-id="element-338">
        <section className="bg-[#17362e] px-5 py-16 text-white sm:px-8 lg:px-12 lg:py-24" data-id="element-339">
          <div className="mx-auto max-w-[1180px]" data-id="element-340">
            <p className="text-[10px] font-bold uppercase tracking-[0.27em] text-[#c8dfbd]" data-id="element-341">
              Contact us
            </p>
            <h1 className="mt-5 max-w-3xl font-serif text-5xl leading-[0.98] sm:text-6xl lg:text-7xl" data-id="element-342">
              Let&apos;s find your way into Nepal.
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-8 text-white/75" data-id="element-343">
              Tell us what is calling you—high passes, slow village days, a
              family holiday, or a route still taking shape. We&apos;ll reply
              with practical next steps.
            </p>
          </div>
        </section>

        <section className="px-5 py-20 sm:px-8 sm:py-28 lg:px-12" data-id="element-344">
          <div className="mx-auto grid max-w-[1180px] gap-12 lg:grid-cols-[0.72fr_1.28fr] lg:gap-20" data-id="element-345">
            <aside aria-label="Contact information" data-id="element-346">
              <SectionHeading eyebrow="Start a conversation" title="We are here to help you plan with confidence." data-id="element-347" />
              <div className="mt-10 space-y-6 border-t border-[#cfd3c8] pt-7" data-id="element-348">
                <a className="flex gap-4 text-[#52635a] transition-colors hover:text-[#315c3b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#4f8057]" href="mailto:hello@tourinnepal.com" data-id="element-349">
                  <MailIcon className="mt-1 h-5 w-5 shrink-0 text-[#4f8057]" aria-hidden="true" data-id="element-350" />
                  <span data-id="element-351">
                    <strong className="block text-[#17362e]" data-id="element-352">Email us</strong>
                    hello@tourinnepal.com
                  </span>
                </a>
                <a className="flex gap-4 text-[#52635a] transition-colors hover:text-[#315c3b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#4f8057]" href="tel:+9779800000000" data-id="element-353">
                  <PhoneIcon className="mt-1 h-5 w-5 shrink-0 text-[#4f8057]" aria-hidden="true" data-id="element-354" />
                  <span data-id="element-355">
                    <strong className="block text-[#17362e]" data-id="element-356">Call us</strong>
                    +977 980 000 0000
                  </span>
                </a>
                <p className="flex gap-4 text-[#52635a]" data-id="element-357">
                  <MapPinIcon className="mt-1 h-5 w-5 shrink-0 text-[#4f8057]" aria-hidden="true" data-id="element-358" />
                  <span data-id="element-359">
                    <strong className="block text-[#17362e]" data-id="element-360">Find us</strong>
                    Thamel, Kathmandu, Nepal
                  </span>
                </p>
                <p className="flex gap-4 text-[#52635a]" data-id="element-361">
                  <Clock3Icon className="mt-1 h-5 w-5 shrink-0 text-[#4f8057]" aria-hidden="true" data-id="element-362" />
                  <span data-id="element-363">
                    <strong className="block text-[#17362e]" data-id="element-364">
                      Response time
                    </strong>
                    Usually within one working day
                  </span>
                </p>
              </div>
            </aside>

            <section id="plan-trip" aria-labelledby="plan-trip-heading" className="scroll-mt-28 border border-[#cfd3c8] bg-[#f4faef] p-6 sm:p-9 lg:p-10" data-id="element-365">
              <div className="flex items-start justify-between gap-5" data-id="element-366">
                <div data-id="element-367">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4f8057]" data-id="element-368">
                    Plan a trip
                  </p>
                  <h2 ref={formHeadingRef} id="plan-trip-heading" tabIndex={-1} className="mt-3 font-serif text-4xl text-[#17362e] outline-none" data-id="element-369">
                    Tell us your starting point
                  </h2>
                </div>
                <SendIcon className="h-6 w-6 shrink-0 text-[#4f8057]" aria-hidden="true" data-id="element-370" />
              </div>
              <p className="mt-3 leading-7 text-[#52635a]" data-id="element-371">
                The more you share, the more useful our first reply can be.
                Fields marked with an asterisk are required.
              </p>

              {submitted && <div role="status" className="mt-6 flex gap-3 border border-[#a9cba7] bg-[#e1efdc] p-4 text-[#17362e]" data-id="element-372">
                  <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#315c3b]" aria-hidden="true" data-id="element-373" />
                  <p data-id="element-374">
                    <strong data-id="element-375">Thank you—your inquiry is ready.</strong> Our travel
                    team will be in touch within one working day.
                  </p>
                </div>}

              <form className="mt-8 grid gap-5 sm:grid-cols-2" noValidate onSubmit={handleSubmit} data-id="element-376">
                <Field label="Full name" required error={errors.name} data-id="element-377">
                  <input value={form.name} onChange={event => updateField('name', event.target.value)} autoComplete="name" className={inputClass} aria-invalid={Boolean(errors.name)} data-id="element-378" />
                </Field>
                <Field label="Email address" required error={errors.email} data-id="element-379">
                  <input type="email" value={form.email} onChange={event => updateField('email', event.target.value)} autoComplete="email" className={inputClass} aria-invalid={Boolean(errors.email)} data-id="element-380" />
                </Field>
                <Field label="Phone number" error={errors.phone} data-id="element-381">
                  <input type="tel" value={form.phone} onChange={event => updateField('phone', event.target.value)} autoComplete="tel" className={inputClass} data-id="element-382" />
                </Field>
                <Field label="Ideal dates" error={errors.travelDates} data-id="element-383">
                  <input value={form.travelDates} onChange={event => updateField('travelDates', event.target.value)} placeholder="e.g. October 2026" className={inputClass} data-id="element-384" />
                </Field>
                <Field label="Number of travellers" error={errors.travellers} data-id="element-385">
                  <select value={form.travellers} onChange={event => updateField('travellers', event.target.value)} className={inputClass} data-id="element-386">
                    <option value="" data-id="element-387">Select</option>
                    <option data-id="element-388">1</option>
                    <option data-id="element-389">2</option>
                    <option data-id="element-390">3–5</option>
                    <option data-id="element-391">6+</option>
                  </select>
                </Field>
                <Field label="I'm interested in" required error={errors.interests} data-id="element-392">
                  <select value={form.interests} onChange={event => updateField('interests', event.target.value)} className={inputClass} aria-invalid={Boolean(errors.interests)} data-id="element-393">
                    <option value="" data-id="element-394">Choose a journey</option>
                    <option data-id="element-395">Trekking</option>
                    <option data-id="element-396">Cultural touring</option>
                    <option data-id="element-397">Honeymoon or private travel</option>
                    <option data-id="element-398">Custom itinerary</option>
                  </select>
                </Field>
                <div className="sm:col-span-2" data-id="element-399">
                  <Field label="What would make this trip meaningful?" required error={errors.message} data-id="element-400">
                    <textarea value={form.message} onChange={event => updateField('message', event.target.value)} rows={5} className={`${inputClass} resize-y`} aria-invalid={Boolean(errors.message)} data-id="element-401" />
                  </Field>
                </div>
                <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-4 border-t border-[#cfd3c8] pt-6" data-id="element-402">
                  <p className="text-sm leading-6 text-[#66766c]" data-id="element-403">
                    By sending this form, you&apos;re asking our team to contact
                    you about your trip.
                  </p>
                  <button type="submit" className="inline-flex items-center gap-2 bg-[#17362e] px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#315c3b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#4f8057]" data-id="element-404">
                    Send inquiry
                    <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-405" />
                  </button>
                </div>
              </form>
            </section>
          </div>
        </section>
      </main>
      <Footer data-id="element-406" />
    </div>;
}
type FieldProps = {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
};
function Field({
  label,
  required,
  error,
  children
}: FieldProps) {
  return <label className="block text-sm font-semibold text-[#17362e]" data-id="element-407">
      {label}
      {required ? <span aria-hidden="true" data-id="element-408"> *</span> : null}
      <span className="mt-2 block" data-id="element-409">{children}</span>
      {error ? <span className="mt-1 block font-normal text-rose-700" data-id="element-410">{error}</span> : null}
    </label>;
}